
import React, { useState } from 'react';
import { Heart, Image as ImageIcon, MessageSquareText, PlusCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Patient } from '@/types/doctor';

interface PatientMedicalInfoProps {
  patient: Patient & {
    profile?: any;
    documents?: any[];
    medicalNotes?: any[];
    message?: string; // Add optional message property
  };
}

const PatientMedicalInfo: React.FC<PatientMedicalInfoProps> = ({ patient }) => {
  const profile = patient.profile || {};
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  
  // Filter image documents for the gallery
  const imageDocuments = patient.documents?.filter(doc => 
    doc.type === 'photo' || doc.type === 'xray'
  ) || [];
  
  const openImageDialog = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setImageDialogOpen(true);
  };
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center">
            <Heart className="h-5 w-5 mr-2" />
            Medical Conditions
          </CardTitle>
          <CardDescription>Patient-reported medical history</CardDescription>
        </CardHeader>
        <CardContent>
          {profile.medicalConditions && profile.medicalConditions.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {profile.medicalConditions.map((condition: string, index: number) => (
                <Badge key={index} variant="outline">{condition}</Badge>
              ))}
            </div>
          ) : (
            <div className="py-2 text-muted-foreground">
              No medical conditions reported
            </div>
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center">
            <MessageSquareText className="h-5 w-5 mr-2" />
            Patient Message
          </CardTitle>
          <CardDescription>Initial request and details from patient</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-3 border rounded-md bg-muted/20">
            <p className="italic">
              {patient.condition}
              {patient.message && <span> - {patient.message}</span>}
              {!patient.message && <span> - Patient is interested in treatment for this condition.</span>}
            </p>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center">
              <ImageIcon className="h-5 w-5 mr-2" />
              Uploaded Images
            </CardTitle>
            <Button variant="outline" size="sm" className="h-8">
              <PlusCircle className="h-4 w-4 mr-1" /> Add Images
            </Button>
          </div>
          <CardDescription>X-rays, photos, and other images</CardDescription>
        </CardHeader>
        <CardContent>
          {imageDocuments.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {imageDocuments.map((doc) => (
                <div key={doc.id} className="relative group">
                  <div 
                    className="aspect-square border rounded-md overflow-hidden cursor-pointer"
                    onClick={() => openImageDialog(doc.url)}
                  >
                    <img 
                      src={doc.url || '/placeholder.svg'} 
                      alt={doc.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="mt-1 text-xs truncate">{doc.name}</div>
                  <Badge 
                    variant="outline" 
                    className="absolute top-2 right-2 bg-background/90 text-xs"
                  >
                    {doc.type}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No images uploaded
            </div>
          )}
        </CardContent>
      </Card>
      
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Image View</DialogTitle>
          </DialogHeader>
          <div className="p-2 flex items-center justify-center">
            <img 
              src={selectedImage || '/placeholder.svg'} 
              alt="Enlarged view" 
              className="max-h-[70vh] max-w-full object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientMedicalInfo;
